import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { getDashboardStats, getFighters, getFights, getTrainingCamps, getAssessments, trackFeatureUsage } from '../services/api';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  Calendar, 
  ClipboardList, 
  Swords,
  TrendingUp,
  ArrowRight,
  Plus,
  Crown,
  AlertTriangle,
  Scale,
  Target,
  Flame,
  ChevronRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  Zap,
  Activity,
  Dumbbell
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  RadarChart, 
  PolarGrid, 
  PolarAngleAxis, 
  Radar, 
  ResponsiveContainer
} from 'recharts';
import { format, differenceInDays, isAfter, isBefore, addDays } from 'date-fns';
import OnboardingTour from '../components/OnboardingTour';
import { cn } from '../lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;

// Action Alert Component
const ActionAlert = ({ type, title, description, action, actionLabel, icon: Icon, priority }) => (
  <motion.div
    initial={{ opacity: 0, x: -10 }}
    animate={{ opacity: 1, x: 0 }}
    className={cn(
      "flex items-center gap-4 p-4 rounded-sm border transition-all hover:border-opacity-80",
      priority === 'high' && "bg-red-600/10 border-red-600/30",
      priority === 'medium' && "bg-yellow-600/10 border-yellow-600/30",
      priority === 'low' && "bg-blue-600/10 border-blue-600/30"
    )}
  >
    <div className={cn(
      "w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0",
      priority === 'high' && "bg-red-600/20",
      priority === 'medium' && "bg-yellow-600/20",
      priority === 'low' && "bg-blue-600/20"
    )}>
      <Icon className={cn(
        "w-5 h-5",
        priority === 'high' && "text-red-500",
        priority === 'medium' && "text-yellow-500",
        priority === 'low' && "text-blue-500"
      )} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-white font-medium text-sm">{title}</p>
      <p className="text-zinc-400 text-xs truncate">{description}</p>
    </div>
    {action && (
      <Link to={action}>
        <Button size="sm" variant="ghost" className="text-zinc-400 hover:text-white flex-shrink-0">
          {actionLabel || 'View'}
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </Link>
    )}
  </motion.div>
);

// Stat Card Component  
const StatCard = ({ title, value, icon: Icon, link, highlight }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    className={cn(
      "bg-ba-surface border p-5 rounded-sm hover:border-red-600/50 hover:shadow-red-glow transition-all group",
      highlight ? "border-red-600/30" : "border-ba-border"
    )}
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-zinc-500 font-body mb-1">{title}</p>
        <p className="font-heading text-3xl text-white tabular-nums">{value}</p>
      </div>
      <div className={cn(
        "w-10 h-10 border rounded-sm flex items-center justify-center group-hover:bg-red-600/20 transition-colors",
        highlight ? "bg-red-600/20 border-red-600/30" : "bg-red-600/10 border-red-600/20"
      )}>
        <Icon className="w-5 h-5 text-red-500" />
      </div>
    </div>
    {link && (
      <Link to={link} className="flex items-center gap-1 text-zinc-500 hover:text-red-500 text-xs mt-3 transition-colors font-body tracking-wide uppercase">
        View all <ArrowRight className="w-3 h-3" />
      </Link>
    )}
  </motion.div>
);

// Empty State Component for Guided Onboarding
const GuidedEmptyState = ({ step, fightersCount, campsCount }) => {
  const navigate = useNavigate();
  
  // Determine current step
  let currentStep = 1;
  let title = "Build Your Fight Team";
  let description = "Start by adding your first fighter to the roster";
  let actionLabel = "Add First Fighter";
  let actionPath = "/fighters/new";
  let icon = Users;
  
  if (fightersCount > 0 && campsCount === 0) {
    currentStep = 2;
    title = "Create Your First Fight Camp";
    description = "Now that you have fighters, set up a training camp for an upcoming bout";
    actionLabel = "Start Fight Camp";
    actionPath = "/training-camps/new";
    icon = Target;
  } else if (fightersCount > 0 && campsCount > 0) {
    currentStep = 3;
    title = "Run Your First Assessment";
    description = "Track your fighter's progress with skill assessments";
    actionLabel = "Run Assessment";
    actionPath = "/assessments/new";
    icon = ClipboardList;
  }
  
  const Icon = icon;
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-red-900/20 border border-zinc-800 rounded-sm p-8 md:p-12"
    >
      <div className="max-w-2xl mx-auto text-center">
        {/* Step Indicator */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={cn(
                "w-8 h-1 rounded-full transition-all",
                s <= currentStep ? "bg-red-600" : "bg-zinc-700"
              )}
            />
          ))}
        </div>
        
        <div className="w-16 h-16 mx-auto mb-6 bg-red-600/20 border border-red-600/30 rounded-sm flex items-center justify-center">
          <Icon className="w-8 h-8 text-red-500" />
        </div>
        
        <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-2">
          Step {currentStep} of 3
        </p>
        
        <h2 className="font-heading text-3xl md:text-4xl text-white tracking-tight mb-4">
          {title}
        </h2>
        
        <p className="text-zinc-400 font-body mb-8 max-w-md mx-auto">
          {description}
        </p>
        
        <Button
          onClick={() => navigate(actionPath)}
          className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider px-8 py-6 text-lg transition-all"
          data-testid="guided-action-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          {actionLabel}
        </Button>
        
        <div className="mt-8 pt-8 border-t border-zinc-800 flex items-center justify-center gap-8 text-sm">
          <div className="flex items-center gap-2">
            <CheckCircle2 className={cn("w-4 h-4", fightersCount > 0 ? "text-green-500" : "text-zinc-600")} />
            <span className={fightersCount > 0 ? "text-zinc-300" : "text-zinc-500"}>Add Fighters</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className={cn("w-4 h-4", campsCount > 0 ? "text-green-500" : "text-zinc-600")} />
            <span className={campsCount > 0 ? "text-zinc-300" : "text-zinc-500"}>Create Camps</span>
          </div>
          <div className="flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-zinc-600" />
            <span className="text-zinc-500">Track Progress</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export const DashboardPage = () => {
  const { gym, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [fighters, setFighters] = useState([]);
  const [camps, setCamps] = useState([]);
  const [fights, setFights] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [actionAlerts, setActionAlerts] = useState([]);
  const [performanceChartType, setPerformanceChartType] = useState('mma'); // 'mma' or 'bjj'

  const loadData = useCallback(async () => {
    try {
      const [statsRes, fightersRes, fightsRes, campsRes, assessmentsRes] = await Promise.all([
        getDashboardStats(),
        getFighters(),
        getFights(),
        getTrainingCamps(),
        getAssessments()
      ]);
      setStats(statsRes.data);
      setFighters(fightersRes.data);
      setFights(fightsRes.data);
      setCamps(campsRes.data);
      setAssessments(assessmentsRes.data);
      
      // Generate action alerts
      generateActionAlerts(fightersRes.data, campsRes.data, fightsRes.data, assessmentsRes.data);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    checkOnboarding();
    trackFeatureUsage('dashboard', 'view').catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkOnboarding = async () => {
    try {
      const response = await axios.get(`${API}/api/auth/onboarding-status`);
      if (!response.data.onboarding_complete) {
        setShowOnboarding(true);
      }
    } catch (error) {
      // Ignore errors
    }
  };

  const generateActionAlerts = (fighters, camps, fights, assessments) => {
    const alerts = [];
    const now = new Date();
    
    // 1. Fighters without assigned camps (if they have upcoming fights)
    const upcomingFights = fights.filter(f => !f.result && isAfter(new Date(f.fight_date), now));
    upcomingFights.forEach(fight => {
      const fighter = fighters.find(f => f.id === fight.fighter_id);
      const hasCamp = camps.some(c => 
        c.fighter_id === fight.fighter_id && 
        (!c.end_date || isAfter(new Date(c.end_date), now))
      );
      if (fighter && !hasCamp) {
        alerts.push({
          id: `no-camp-${fight.id}`,
          type: 'no_camp',
          priority: 'high',
          title: `${fighter.name} needs a fight camp`,
          description: `Fight vs ${fight.opponent_name} on ${format(new Date(fight.fight_date), 'M/d/yyyy')} - No active camp`,
          action: '/training-camps/new',
          actionLabel: 'Create Camp',
          icon: Target
        });
      }
    });
    
    // 2. Fights coming up soon (within 14 days)
    upcomingFights.forEach(fight => {
      const daysUntil = differenceInDays(new Date(fight.fight_date), now);
      if (daysUntil <= 14 && daysUntil > 0) {
        const fighter = fighters.find(f => f.id === fight.fighter_id);
        alerts.push({
          id: `fight-soon-${fight.id}`,
          type: 'fight_soon',
          priority: daysUntil <= 7 ? 'high' : 'medium',
          title: `${fighter?.name || 'Fighter'} fights in ${daysUntil} days`,
          description: `vs ${fight.opponent_name} at ${fight.event_name}`,
          action: `/fights/${fight.id}`,
          actionLabel: 'View',
          icon: Swords
        });
      }
    });
    
    // 3. Fighters behind on weight (if they have upcoming fights within 30 days)
    upcomingFights.forEach(fight => {
      const daysUntil = differenceInDays(new Date(fight.fight_date), now);
      if (daysUntil <= 30) {
        const fighter = fighters.find(f => f.id === fight.fighter_id);
        if (fighter && fighter.current_weight && fighter.weight_class) {
          // Parse target weight from weight class (e.g., "Lightweight (155)" -> 155)
          const targetMatch = fighter.weight_class.match(/\((\d+)\)/);
          if (targetMatch) {
            const targetWeight = parseInt(targetMatch[1]);
            const overWeight = fighter.current_weight - targetWeight;
            if (overWeight > 5) {
              alerts.push({
                id: `weight-${fighter.id}`,
                type: 'weight',
                priority: overWeight > 10 ? 'high' : 'medium',
                title: `${fighter.name} is ${overWeight.toFixed(1)}lbs over`,
                description: `Current: ${fighter.current_weight}lbs, Target: ${targetWeight}lbs`,
                action: '/weight-tracking',
                actionLabel: 'Track',
                icon: Scale
              });
            }
          }
        }
      }
    });
    
    // 4. Fighters without recent assessments (in last 30 days)
    fighters.forEach(fighter => {
      const recentAssessment = assessments.find(a => 
        a.fighter_id === fighter.id && 
        differenceInDays(now, new Date(a.date)) <= 30
      );
      if (!recentAssessment && fighters.length <= 10) { // Only show for smaller rosters
        alerts.push({
          id: `assessment-${fighter.id}`,
          type: 'assessment',
          priority: 'low',
          title: `${fighter.name} needs assessment`,
          description: 'No assessment in the last 30 days',
          action: '/assessments/new',
          actionLabel: 'Assess',
          icon: ClipboardList
        });
      }
    });
    
    // 5. Active camps ending soon
    camps.forEach(camp => {
      if (camp.end_date) {
        const daysUntilEnd = differenceInDays(new Date(camp.end_date), now);
        if (daysUntilEnd <= 7 && daysUntilEnd > 0) {
          const fighter = fighters.find(f => f.id === camp.fighter_id);
          alerts.push({
            id: `camp-end-${camp.id}`,
            type: 'camp_ending',
            priority: 'medium',
            title: `${camp.name} ends in ${daysUntilEnd} days`,
            description: fighter ? `Camp for ${fighter.name}` : 'Training camp ending soon',
            action: `/training-camps/${camp.id}`,
            actionLabel: 'View',
            icon: Calendar
          });
        }
      }
    });
    
    // Sort by priority and limit
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    alerts.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
    setActionAlerts(alerts.slice(0, 5));
  };

  // Prepare radar chart data - uses new assessment criteria based on type
  const getAverageAssessments = (chartType = null) => {
    if (!stats?.recent_assessments?.length) return null;
    const assessmentsData = stats.recent_assessments;
    
    // Use specified chart type, or fall back to gym's sport type
    const displayType = chartType || (gym?.sport_type === 'bjj' ? 'bjj' : 'mma');
    
    // Check if assessments have the typed format
    const hasNewMMAFields = assessmentsData.some(a => a.striking_offense !== undefined);
    const hasNewBJJFields = assessmentsData.some(a => a.guard_play !== undefined);
    const hasLegacyFields = assessmentsData.some(a => a.striking !== undefined && a.assessment_type === undefined);
    
    // BJJ Assessment Data
    if (displayType === 'bjj' && hasNewBJJFields) {
      const bjjAssessments = assessmentsData.filter(a => a.assessment_type === 'bjj' || a.guard_play !== undefined);
      if (bjjAssessments.length > 0) {
        const bjjFields = [
          { key: 'guard_play', label: 'Guard Play' },
          { key: 'passing', label: 'Passing' },
          { key: 'submissions', label: 'Submissions' },
          { key: 'sweeps', label: 'Sweeps' },
          { key: 'takedowns', label: 'Takedowns' },
          { key: 'escapes', label: 'Escapes' },
          { key: 'positional_control', label: 'Position Ctrl' },
          { key: 'bjj_cardio', label: 'Cardio' },
        ];
        
        const result = bjjFields.map(f => ({
          skill: f.label,
          value: Math.round(bjjAssessments.reduce((a, b) => a + (b[f.key] || 0), 0) / bjjAssessments.length) || 0
        })).filter(d => d.value > 0);
        
        if (result.length > 0) return result;
      }
    }
    
    // MMA Assessment Data
    if (displayType === 'mma' && hasNewMMAFields) {
      const mmaAssessments = assessmentsData.filter(a => a.assessment_type === 'mma' || a.striking_offense !== undefined);
      if (mmaAssessments.length > 0) {
        const mmaFields = [
          { key: 'striking_offense', label: 'Striking Off.' },
          { key: 'striking_defense', label: 'Striking Def.' },
          { key: 'clinch_offense', label: 'Clinch Off.' },
          { key: 'clinch_defense', label: 'Clinch Def.' },
          { key: 'wrestling_offense', label: 'Wrestling Off.' },
          { key: 'takedown_defense', label: 'TD Defense' },
          { key: 'ground_control', label: 'Ground Ctrl' },
          { key: 'submission_offense', label: 'Sub Off.' },
          { key: 'submission_defense', label: 'Sub Def.' },
          { key: 'guard_bottom_game', label: 'Guard/Bottom' },
          { key: 'mma_cardio', label: 'Cardio' },
          { key: 'fight_iq', label: 'Fight IQ' },
        ];
        
        const result = mmaFields.map(f => ({
          skill: f.label,
          value: Math.round(mmaAssessments.reduce((a, b) => a + (b[f.key] || 0), 0) / mmaAssessments.length) || 0
        })).filter(d => d.value > 0);
        
        if (result.length > 0) return result;
      }
    }
    
    // Fallback to legacy format if no typed assessments
    if (hasLegacyFields) {
      const avg = {
        striking: Math.round(assessmentsData.reduce((a, b) => a + (b.striking || 0), 0) / assessmentsData.length),
        grappling: Math.round(assessmentsData.reduce((a, b) => a + (b.grappling || 0), 0) / assessmentsData.length),
        wrestling: Math.round(assessmentsData.reduce((a, b) => a + (b.wrestling || 0), 0) / assessmentsData.length),
        cardio: Math.round(assessmentsData.reduce((a, b) => a + (b.cardio || 0), 0) / assessmentsData.length),
      };
      if (avg.striking > 0 || avg.grappling > 0 || avg.wrestling > 0 || avg.cardio > 0) {
        return [
          { skill: 'Striking', value: avg.striking },
          { skill: 'Grappling', value: avg.grappling },
          { skill: 'Wrestling', value: avg.wrestling },
          { skill: 'Cardio', value: avg.cardio },
        ];
      }
    }
    
    return null;
  };
  
  // Check if we have data for each type
  const hasMMAData = stats?.recent_assessments?.some(a => a.striking_offense !== undefined || a.assessment_type === 'mma');
  const hasBJJData = stats?.recent_assessments?.some(a => a.guard_play !== undefined || a.assessment_type === 'bjj');

  const radarData = getAverageAssessments(performanceChartType);
  
  // Get active camps
  const activeCamps = camps.filter(c => {
    if (!c.end_date) return true;
    return isAfter(new Date(c.end_date), new Date());
  });
  
  // Get upcoming fights
  const upcomingFights = fights.filter(f => !f.result).slice(0, 3);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  const needsSubscription = !gym?.subscription_tier || gym.subscription_tier === 'none' || gym.subscription_status !== 'active';
  const isNewUser = fighters.length === 0;
  const needsGuidance = fighters.length === 0 || (fighters.length > 0 && camps.length === 0);

  return (
    <DashboardLayout>
      {/* Onboarding Tour */}
      {showOnboarding && (
        <OnboardingTour onComplete={() => setShowOnboarding(false)} />
      )}
      
      <div className="space-y-6" data-testid="dashboard-page">
        {/* Header - Fight Camp Command Center */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-red-500 font-body mb-1">
              Fight Camp Command Center
            </p>
            <h1 className="font-heading text-3xl md:text-4xl text-white tracking-tight">
              PERFORMANCE HUB
            </h1>
            <p className="text-zinc-500 mt-1 font-body text-sm">
              {gym?.name || 'Your Fight Team'} • {fighters.length} Fighter{fighters.length !== 1 ? 's' : ''} • {activeCamps.length} Active Camp{activeCamps.length !== 1 ? 's' : ''}
            </p>
          </div>
          
          {/* Primary CTAs */}
          {isAdmin && !isNewUser && (
            <div className="flex items-center gap-3">
              <Link to="/fighters/new">
                <Button 
                  variant="outline" 
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 uppercase font-heading tracking-wider"
                  data-testid="add-fighter-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Fighter
                </Button>
              </Link>
              <Link to="/training-camps/new">
                <Button 
                  className="bg-red-600 hover:bg-red-700 hover:shadow-red-glow text-white uppercase font-heading tracking-wider px-6"
                  data-testid="start-camp-btn"
                >
                  <Target className="w-4 h-4 mr-2" />
                  Start Fight Camp
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Subscription Banner */}
        {needsSubscription && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-gradient-to-r from-red-600/10 to-yellow-600/10 border border-red-600/30 p-4 rounded-sm"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-3">
                <Crown className="w-6 h-6 text-yellow-500" />
                <div>
                  <p className="text-white font-medium">Unlock Elite Features</p>
                  <p className="text-zinc-400 text-sm">Full access to all performance tools</p>
                </div>
              </div>
              <Link to="/subscription">
                <Button className="bg-red-600 hover:bg-red-700 text-white uppercase font-heading font-semibold" data-testid="subscribe-banner-btn">
                  View Plans
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {/* Guided Empty State for New Users */}
        {needsGuidance && (
          <GuidedEmptyState 
            fightersCount={fighters.length} 
            campsCount={camps.length}
          />
        )}

        {/* Action Alerts - "What do I need to do right now?" */}
        {!needsGuidance && actionAlerts.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-zinc-900/50 border border-zinc-800 rounded-sm overflow-hidden"
          >
            <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-red-600/20 rounded-sm flex items-center justify-center">
                  <Zap className="w-4 h-4 text-red-500" />
                </div>
                <div>
                  <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                    Camp Focus
                  </h3>
                  <p className="text-zinc-500 text-xs">Actions requiring your attention</p>
                </div>
              </div>
              <span className="px-2 py-1 bg-red-600/20 text-red-500 text-xs rounded-sm font-medium">
                {actionAlerts.length} item{actionAlerts.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="p-4 space-y-3">
              {actionAlerts.map((alert) => (
                <ActionAlert key={alert.id} {...alert} />
              ))}
            </div>
          </motion.div>
        )}

        {/* Stats Grid - Prioritized */}
        {!needsGuidance && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard 
              title="Active Fight Team" 
              value={stats?.fighters_count || 0}
              icon={Users}
              link="/fighters"
              highlight={true}
            />
            <StatCard 
              title="Active Camps" 
              value={activeCamps.length}
              icon={Target}
              link="/training-camps"
              highlight={activeCamps.length > 0}
            />
            <StatCard 
              title="Assessments" 
              value={stats?.total_assessments || 0}
              icon={ClipboardList}
              link="/assessments"
            />
            <StatCard 
              title="Upcoming Bouts" 
              value={stats?.upcoming_fights || 0}
              icon={Swords}
              link="/fights"
            />
          </div>
        )}

        {/* Main Content Grid */}
        {!needsGuidance && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Active Fight Camps - Primary Focus */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-7 bg-zinc-900/50 border border-zinc-800 rounded-sm"
            >
              <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-600/20 rounded-sm flex items-center justify-center">
                    <Target className="w-4 h-4 text-red-500" />
                  </div>
                  <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                    Active Fight Camps
                  </h3>
                </div>
                <Link to="/training-camps" className="text-zinc-400 hover:text-red-500 text-xs transition-colors uppercase tracking-wider">
                  View all
                </Link>
              </div>
              
              {activeCamps.length > 0 ? (
                <div className="divide-y divide-zinc-800">
                  {activeCamps.slice(0, 4).map((camp) => {
                    const fighter = fighters.find(f => f.id === camp.fighter_id);
                    const daysLeft = camp.end_date ? differenceInDays(new Date(camp.end_date), new Date()) : null;
                    return (
                      <Link
                        key={camp.id}
                        to={`/training-camps/${camp.id}`}
                        className="flex items-center justify-between p-4 hover:bg-zinc-800/30 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                            <Activity className="w-5 h-5 text-red-500" />
                          </div>
                          <div>
                            <p className="text-white font-medium">{camp.name}</p>
                            <p className="text-zinc-500 text-sm">{fighter?.name || 'Fighter'}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          {daysLeft !== null && daysLeft > 0 ? (
                            <>
                              <p className="text-zinc-300 text-sm font-medium">{daysLeft} days left</p>
                              <p className="text-zinc-500 text-xs">Camp Progress</p>
                            </>
                          ) : (
                            <span className="px-2 py-1 bg-green-600/20 text-green-500 text-xs rounded-sm">Active</span>
                          )}
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Target className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                  <p className="text-zinc-400 mb-4">No active fight camps</p>
                  {isAdmin && (
                    <Link to="/training-camps/new">
                      <Button className="bg-red-600 hover:bg-red-700" data-testid="empty-start-camp">
                        <Plus className="w-4 h-4 mr-2" />
                        Start Fight Camp
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </motion.div>

            {/* Team Performance Radar */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-5 bg-zinc-900/50 border border-zinc-800 rounded-sm"
            >
              <div className="px-5 py-4 border-b border-zinc-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn(
                      "w-8 h-8 rounded-sm flex items-center justify-center",
                      performanceChartType === 'mma' ? "bg-red-600/20" : "bg-blue-600/20"
                    )}>
                      {performanceChartType === 'mma' ? (
                        <Swords className="w-4 h-4 text-red-500" />
                      ) : (
                        <Dumbbell className="w-4 h-4 text-blue-500" />
                      )}
                    </div>
                    <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                      Team Performance
                    </h3>
                  </div>
                  
                  {/* MMA/BJJ Toggle */}
                  <div className="flex items-center gap-1 bg-zinc-800/50 p-1 rounded-sm" data-testid="performance-chart-toggle">
                    <button
                      onClick={() => setPerformanceChartType('mma')}
                      disabled={!hasMMAData}
                      className={cn(
                        "px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-all flex items-center gap-1.5",
                        performanceChartType === 'mma' 
                          ? "bg-red-600 text-white shadow-lg shadow-red-600/20" 
                          : "text-zinc-400 hover:text-white hover:bg-zinc-700",
                        !hasMMAData && "opacity-50 cursor-not-allowed"
                      )}
                      data-testid="toggle-mma"
                    >
                      <Swords className="w-3 h-3" />
                      MMA
                    </button>
                    <button
                      onClick={() => setPerformanceChartType('bjj')}
                      disabled={!hasBJJData}
                      className={cn(
                        "px-3 py-1.5 text-xs font-bold uppercase tracking-wider rounded-sm transition-all flex items-center gap-1.5",
                        performanceChartType === 'bjj' 
                          ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20" 
                          : "text-zinc-400 hover:text-white hover:bg-zinc-700",
                        !hasBJJData && "opacity-50 cursor-not-allowed"
                      )}
                      data-testid="toggle-bjj"
                    >
                      <Dumbbell className="w-3 h-3" />
                      BJJ
                    </button>
                  </div>
                </div>
              </div>
              {radarData ? (
                <div className="h-64 p-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radarData}>
                      <PolarGrid stroke="#3f3f46" />
                      <PolarAngleAxis dataKey="skill" tick={{ fill: '#a1a1aa', fontSize: 11 }} />
                      <Radar
                        name={performanceChartType === 'mma' ? 'MMA Skills' : 'BJJ Skills'}
                        dataKey="value"
                        stroke={performanceChartType === 'mma' ? '#dc2626' : '#2563eb'}
                        fill={performanceChartType === 'mma' ? '#dc2626' : '#2563eb'}
                        fillOpacity={0.3}
                      />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-64 flex flex-col items-center justify-center p-4">
                  <ClipboardList className="w-10 h-10 mb-3 text-zinc-600" />
                  <p className="text-zinc-400 text-center mb-2">
                    {performanceChartType === 'bjj' && !hasBJJData 
                      ? 'No BJJ assessments found' 
                      : performanceChartType === 'mma' && !hasMMAData
                        ? 'No MMA assessments found'
                        : 'Run assessments to see team performance'
                    }
                  </p>
                  <p className="text-zinc-500 text-xs text-center mb-4">
                    {(hasMMAData || hasBJJData) 
                      ? `Try switching to ${performanceChartType === 'bjj' ? 'MMA' : 'BJJ'} view`
                      : 'Start with fighter assessments'
                    }
                  </p>
                  {isAdmin && (
                    <Link to="/assessments/new">
                      <Button variant="outline" className="border-zinc-700" data-testid="empty-assessment">
                        <Plus className="w-4 h-4 mr-2" />
                        Run Assessment
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}

        {/* Upcoming Fights & Fighter Roster */}
        {!needsGuidance && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Upcoming Fights */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-sm"
            >
              <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-600/20 rounded-sm flex items-center justify-center">
                    <Swords className="w-4 h-4 text-red-500" />
                  </div>
                  <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                    Upcoming Bouts
                  </h3>
                </div>
                <Link to="/fights" className="text-zinc-400 hover:text-red-500 text-xs transition-colors uppercase tracking-wider">
                  View all
                </Link>
              </div>
              {upcomingFights.length > 0 ? (
                <div className="divide-y divide-zinc-800">
                  {upcomingFights.map((fight) => {
                    const fighter = fighters.find(f => f.id === fight.fighter_id);
                    const daysUntil = differenceInDays(new Date(fight.fight_date), new Date());
                    return (
                      <Link
                        key={fight.id}
                        to={`/fights/${fight.id}`}
                        className="flex items-center justify-between p-4 hover:bg-zinc-800/30 transition-colors"
                      >
                        <div>
                          <p className="text-white font-medium">{fighter?.name || 'Fighter'}</p>
                          <p className="text-zinc-400 text-sm">vs {fight.opponent_name}</p>
                        </div>
                        <div className="text-right">
                          <p className={cn(
                            "text-sm font-medium",
                            daysUntil <= 7 ? "text-red-500" : "text-zinc-300"
                          )}>
                            {daysUntil <= 0 ? 'Today!' : `${daysUntil} days`}
                          </p>
                          <p className="text-zinc-500 text-xs">
                            {format(new Date(fight.fight_date), 'M/d/yyyy')}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Swords className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                  <p className="text-zinc-400 mb-4">No upcoming fights scheduled</p>
                  {isAdmin && (
                    <Link to="/fights/new">
                      <Button variant="outline" className="border-zinc-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Schedule Fight
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </motion.div>

            {/* Fighter Roster */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-zinc-900/50 border border-zinc-800 rounded-sm"
            >
              <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-red-600/20 rounded-sm flex items-center justify-center">
                    <Users className="w-4 h-4 text-red-500" />
                  </div>
                  <h3 className="font-heading text-base font-bold text-white uppercase tracking-tight">
                    Active Fight Team
                  </h3>
                </div>
                <Link to="/fighters" className="text-zinc-400 hover:text-red-500 text-xs transition-colors uppercase tracking-wider">
                  View all
                </Link>
              </div>
              {fighters.length > 0 ? (
                <div className="divide-y divide-zinc-800">
                  {fighters.slice(0, 4).map((fighter) => (
                    <Link
                      key={fighter.id}
                      to={`/fighters/${fighter.id}`}
                      className="flex items-center justify-between p-4 hover:bg-zinc-800/30 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-zinc-800 rounded-full flex items-center justify-center">
                          <Users className="w-5 h-5 text-zinc-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{fighter.name}</p>
                          <p className="text-zinc-500 text-sm">{fighter.weight_class}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-zinc-300 text-sm font-mono">
                          {fighter.record_wins}-{fighter.record_losses}-{fighter.record_draws}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center">
                  <Users className="w-10 h-10 mx-auto mb-3 text-zinc-600" />
                  <p className="text-zinc-400 mb-4">No fighters on the roster</p>
                  {isAdmin && (
                    <Link to="/fighters/new">
                      <Button className="bg-red-600 hover:bg-red-700">
                        <Plus className="w-4 h-4 mr-2" />
                        Add First Fighter
                      </Button>
                    </Link>
                  )}
                </div>
              )}
            </motion.div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
